import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {UtilitiesService} from "../utilities.service";
import {
    IDictionaryEntry,
    IPronunciationDictionary,
    IPronunciationMap,
    Pronunciation,
    IExportDictionary,
    ISelection,
    IVocabularyEntry
} from "../../api/data-model";
import {PipelineService} from "./pipeline.service";
import {HttpClient} from "@angular/common/http";
import {IExpansions} from "./batch.pipeline.service";

interface IRating {
    word:string;
    score:number;
}

export class DictionaryPipelineService {

    private pronunciationDictionary:IPronunciationDictionary;
    private phonemes:Object;
    private exportDictionary: IExportDictionary;

    constructor(public http:HttpClient, private contentLocation: string) {
        if (http) {
            this.loadDictionary().subscribe((response:any) => {
                // console.log("setting dict to:" + JSON.stringify(response.json()))
                this.pronunciationDictionary = (<IPronunciationDictionary>response);
            });

            this.loadPhonemes().subscribe((response:Response) => {
                this.phonemes = (<Object>response);
            });
        }
    }

    private loadDictionary():Observable<any> {
        return this.http.get(PipelineService.CONTENT_SERVER + "dictionary" + UtilitiesService.cacheBuster("?"),
            { withCredentials: true });
    }

    public loadExpansions():Observable<any> {
        return this.http.get(PipelineService.CONTENT_SERVER + "expansion" + UtilitiesService.cacheBuster("?"),
            { withCredentials: true });
    }


    private loadPhonemes():Observable<any> {
        return this.http.get(this.contentLocation + "dictionary/Phonemes.json" +
                             UtilitiesService.cacheBuster("?"), );
    }

    public saveDictionary(prefix: string, download: boolean): void {
        let ordered: IPronunciationDictionary = {};
        Object.keys(this.pronunciationDictionary).sort().forEach((key) => {
            ordered[key] = this.pronunciationDictionary[key];
        });
        let dictionaryString = JSON.stringify(ordered, null, 4);
        if (download) {
            UtilitiesService.downloadFile(dictionaryString,
                prefix + "PronunciationDictionary.json");
        } else {
            console.log("posting dictionary to URL: " + PipelineService.CONTENT_SERVER + "dictionary");
            this.http.post(
                PipelineService.CONTENT_SERVER + "dictionary",
                dictionaryString, {headers: {"Content-Type": "application/json", "Accept": "application/json"},
                    withCredentials: true }
            ).subscribe((response:any) => {
                console.log("Saved Dictionary, success:" + response?.ok);
            });
        }
    }

    public saveExpansions(expansions: IExpansions, download: boolean): void {
        let expansionString = JSON.stringify(expansions, null, 4);
        if (download) {
            UtilitiesService.downloadFile(expansionString,
                "expansions.json");
        } else {
            console.log("posting expansions to URL: " + PipelineService.CONTENT_SERVER + "expansions");
            this.http.post(
                PipelineService.CONTENT_SERVER + "expansions",
                expansionString, {headers: {"Content-Type": "application/json", "Accept": "application/json"},
                    withCredentials: true }
            ).subscribe((response:any) => {
                console.log("Saved Expansions, success:" + response?.ok);
            });
        }
    }

    public getNearMatches(word: string, numMatches: number): IDictionaryEntry[] {
        let bestMatchList: IDictionaryEntry[] = [];
        let dictionaryList = Object.keys(this.pronunciationDictionary);
        let ratings = this.findBestMatch(word, dictionaryList);
        let sortedRatings = ratings.sort((a, b) => {
            return b.score - a.score;
        });
        sortedRatings = sortedRatings.slice(0, numMatches);
        sortedRatings.forEach((rating) => {
            let pronunciationList = Object.keys(this.pronunciationDictionary[rating.word]);
            bestMatchList.push({word: rating.word, pronunciations: pronunciationList});
        });
        return bestMatchList;
    }

    private findBestMatch(word: string, list: string[]): IRating[] {
        let ratings:IRating[] = [];
        list.forEach((s) => {
            ratings.push({word: s, score: this.stringSimilarity(word, s)});
        });
        return ratings;
    }

    private stringSimilarity(sa1: string, sa2: string): number {
        // from https://gist.github.com/doorhammer/9957864, refactored to typescript
        // Compare two strings to see how similar they are.
        // Answer is returned as a value from 0 - 1
        // 1 indicates a perfect similarity (100%) while 0 indicates no similarity (0%)
        // Algorithm is set up to closely mimic the mathematical formula from
        // the article describing the algorithm, for clarity.
        // Algorithm source site: http://www.catalysoft.com/articles/StrikeAMatch.html
        // (Most specifically the slightly cryptic variable names were written as such
        // to mirror the mathematical implementation on the source site)
        //
        // 2014-04-03
        // Found out that the algorithm is an implementation of the Sørensen–Dice coefficient [1]
        // [1] http://en.wikipedia.org/wiki/S%C3%B8rensen%E2%80%93Dice_coefficient
        //
        // The algorithm is an n-gram comparison of bigrams of characters in a string

        // for my purposes, comparison should not check case or whitespace
        let s1 = sa1.replace(/\s/g, "").toLowerCase();
        let s2 = sa2.replace(/\s/g, "").toLowerCase();

        let similarity_num = 2 * this.intersect(this.pairs(s1), this.pairs(s2)).length;
        let similarity_den = this.pairs(s1).length + this.pairs(s2).length;
        let similarity = similarity_num / similarity_den;
        return similarity;
    }

    private intersect(arr1: string[], arr2: string[]): string[] {
        // I didn't write this.  I'd like to come back sometime
        // and write my own intersection algorithm.  This one seems
        // clean and fast, though.  Going to try to find out where
        // I got it for attribution.  Not sure right now.
        let r = [], o = {}, l = arr2.length, i, v;
        for (i = 0; i < l; i++) {
            o[arr2[i]] = true;
        }
        l = arr1.length;
        for (i = 0; i < l; i++) {
            v = arr1[i];
            if (v in o) {
                r.push(v);
            }
        }
        return r;
    }

    private pairs(s: string): string[] {
        // Get an array of all pairs of adjacent letters in a string
        let pairs = [];
        for (let i = 0; i < s.length - 1; i++) {
            pairs[i] = s.slice(i, i + 2);
        }
        return pairs;
    }


    public getWord(word:string):IDictionaryEntry {
        if (!word || !this.pronunciationDictionary) {
            return null;
        }
        let upperWord:string = word.toUpperCase();
        let pronunciationMap:IPronunciationMap = this.pronunciationDictionary[upperWord];
        let pronunciations:Pronunciation[] = pronunciationMap ? <Pronunciation[]>Object.keys(pronunciationMap) : [];
        let dictionaryEntry:IDictionaryEntry = {word: upperWord, pronunciations: pronunciations};
        return dictionaryEntry;
    }

    public deleteWord(word:string):Object {
        let upperWord:string = word.toUpperCase();
        if (!this.pronunciationDictionary[upperWord]) {
            return false;
        }
        delete this.pronunciationDictionary[upperWord];
        return true;
    }

    // TBD is this the best way to return values/error conditions?
    public updateWord(dictionaryEntry:IDictionaryEntry):number {
        // split into 3 functions - normalize, check, add
        console.log("trying to add data: " + JSON.stringify(dictionaryEntry));
        console.log("normalizing");
        let normalizedDictionaryEntry:IDictionaryEntry = this.normalizeDictionaryEntry(dictionaryEntry);
        console.log("checking");
        if (!normalizedDictionaryEntry || !this.checkDictionaryEntry(normalizedDictionaryEntry)) {
            return 0;
        }

        console.log("adding");
        return this.updateDictionaryEntry(normalizedDictionaryEntry);
    }

    public addDictionary(selection: ISelection):IExportDictionary {
        this.exportDictionary = {};
        let previousSentence = 0;
        selection.text.forEach((block) => {
            if (block.sentences) {
                block.sentences.forEach((sentence) => {
                    if (Number(sentence.id) !== previousSentence + 1) {
                        console.log(selection.metaData.referenceStory + ": non-consecutive sentences "
                                    + previousSentence.toString() + ":" + sentence.id);
                    }
                    previousSentence = Number(sentence.id);
                    sentence.words.forEach((word) => {
                        this.addDictionaryForWord(selection.metaData.referenceStory, word.word, word.dictionary);

                    });
                });
            }
        });

        if (selection.vocabularyEntries) {
            selection.vocabularyEntries.forEach((entry: IVocabularyEntry) => {
                this.addDictionaryForWord(selection.metaData.referenceStory, entry.word, entry.dictionary);
            });
        }

        return JSON.parse(JSON.stringify(this.exportDictionary));
    }

    private addDictionaryForWord(selectionUniqueName: string, word: string, dictionary: string[]): void {
        let entry: IDictionaryEntry;
        if (dictionary && dictionary.length) {
            dictionary.forEach((dictionaryString) => {
                let words: string[] = dictionaryString.split(" ");
                words.forEach((wordKey) => {
                    entry = this.getWord(wordKey);
                    if (entry && entry.pronunciations && entry.pronunciations.length) {
                        this.exportDictionary[entry.word] = entry.pronunciations;
                    } else {
                        this.exportDictionary[wordKey] = null;
                        console.log(selectionUniqueName + ": no expansion dictionary entry for:" + entry.word);
                    }

                });
            });
        } else {
            entry = this.getWord(word);
            if (entry && entry.pronunciations && entry.pronunciations.length) {
                this.exportDictionary[entry.word] = entry.pronunciations;
            } else {
                if (entry) {
                    this.exportDictionary[entry.word] = null;
                    console.log(selectionUniqueName + ": no dictionary entry for:" + entry.word);
                }
                console.log("No Entry");
            }
        }
    }

    private normalizeDictionaryEntry(dictionaryEntry: IDictionaryEntry): IDictionaryEntry {
        let normalizedData:IDictionaryEntry = {word: "", pronunciations: []};
        if (!dictionaryEntry.word || !dictionaryEntry.pronunciations) {
            console.log("can't add word data - missing word or pronunciations");
            return null;
        }
        normalizedData.word = dictionaryEntry.word.toUpperCase();
        // case normalize
        dictionaryEntry.pronunciations.forEach((pronunciation) => {
            normalizedData.pronunciations.push(pronunciation.toUpperCase());
        });
        return normalizedData;
    }

    public checkDictionaryEntry(dictionaryEntry:IDictionaryEntry):boolean {
        // split into 3 functions - normalize, check, add
        let upperWordValidMatchSet = dictionaryEntry.word.match(/[A-Z0-9!']+/);
        // check for invalid chars in word
        if (!upperWordValidMatchSet || (dictionaryEntry.word !== upperWordValidMatchSet[0])) {
            console.log("word has invalid characters:" + dictionaryEntry.word);
            return false;
        }

        // check for invalid pron, return false.
        // tbd: make phonemes a map
        let allValid = true;
        let validPhonemes:Object = this.phonemes;
        dictionaryEntry.pronunciations.forEach(pronunciation => {
            if (pronunciation === "") {
                allValid = false;
            }
            let pronunciationList:Array<string> = pronunciation.split(" ");
            pronunciationList.forEach(phoneme => {
                // if phoneme not in list, set allValid = false
                if (!validPhonemes[phoneme]) {
                    allValid = false;
                }
            });
        });
        return allValid;
    }

    private updateDictionaryEntry(dictionaryEntry:IDictionaryEntry):number {
        // if word already exists, add new prons.  if no new prons, return false
        // note: pron list should be treated as map . . .
        // if word does not exist, add word and prons.
        let pronunciationsAdded = 0;
        let word:string = dictionaryEntry.word;
        let dictionary:Object = this.pronunciationDictionary;
        // will replace existing pronunciations
        dictionary[word] = {};

        dictionaryEntry.pronunciations.forEach(pronunciation => {
            if (!dictionary[word][pronunciation]) {
                dictionary[word][pronunciation] = false;
                pronunciationsAdded++;
            }

        });
        return pronunciationsAdded;
    }
}
